/**
 * Platform Previews — visual mockups of how a post will appear on each social network.
 *
 * Extracted from PostDetailPage.js (was inline, ~580 LOC). Each preview is a
 * small, self-contained presentational component that takes the post data and
 * a render-helper function as props. Keeping all previews in one file (rather
 * than 7 separate files) because:
 *   1. They share styling conventions and shouldn't drift apart
 *   2. They're never imported individually — always through this index
 *   3. Each one is small (30-100 LOC); 7 separate files would dilute cohesion
 */
import React from 'react';
import { Image, Video, Play, Heart, MessageSquare, Share2, Youtube } from 'lucide-react';
import { TabsContent } from '../ui/tabs';
import { toast } from 'sonner';

/* ----------------------------- helpers ----------------------------- */

const isVideoUrl = (url) =>
  url?.includes('/video/') || url?.includes('.mp4') || url?.includes('.mov') || url?.includes('.webm');

const getVideoId = (url) => {
  if (!url) return null;
  const parts = url.split('/');
  return parts[parts.length - 1]?.replace('.mp4', '').replace('.mov', '').replace('.webm', '');
};

const getVideoThumbnailUrl = (url) => {
  const videoId = getVideoId(url);
  if (!videoId) return null;
  return `${process.env.REACT_APP_BACKEND_URL}/api/video/thumbnail/${videoId}?token=${localStorage.getItem('token')}`;
};

/* ------------------------- shared sub-pieces ----------------------- */

/**
 * Renders the media block used inside several platform previews.
 * Handles 1 image/video and grids of up to 4.
 */
export const renderPostMedia = (post, aspectRatio = 'aspect-video', maxItems = 4) => {
  const mediaUrls = post?.media_urls || [];
  if (mediaUrls.length === 0) return null;
  const displayMedia = mediaUrls.slice(0, maxItems);

  if (displayMedia.length === 1) {
    const url = displayMedia[0];
    const isVideo = isVideoUrl(url);
    return (
      <div className={`${aspectRatio} bg-slate-100 relative overflow-hidden`}>
        {isVideo ? (
          <>
            <img
              src={getVideoThumbnailUrl(url)}
              alt="Video thumbnail"
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                <Play className="h-6 w-6 text-emerald-600 ml-1" />
              </div>
            </div>
          </>
        ) : (
          <img src={url} alt="Media" className="w-full h-full object-cover" />
        )}
      </div>
    );
  }

  return (
    <div className={`grid ${displayMedia.length === 2 ? 'grid-cols-2' : 'grid-cols-2'} gap-0.5`}>
      {displayMedia.map((url, idx) => {
        const isVideo = isVideoUrl(url);
        return (
          <div key={idx} className={`${aspectRatio} bg-slate-100 relative overflow-hidden`}>
            {isVideo ? (
              <>
                <img
                  src={getVideoThumbnailUrl(url)}
                  alt={`Video ${idx + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <Play className="h-6 w-6 text-white" />
                </div>
              </>
            ) : (
              <img src={url} alt={`Media ${idx + 1}`} className="w-full h-full object-cover" />
            )}
            {idx === maxItems - 1 && mediaUrls.length > maxItems && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white font-bold text-lg">+{mediaUrls.length - maxItems}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const previewToast = (platform) =>
  toast.info(`This is a post preview. Interactions will be available once published on ${platform}.`);

/* ------------------------- platform previews ----------------------- */

export const FacebookPreview = ({ post, renderMediaPreview }) => (
  <TabsContent value="facebook">
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden max-w-md mx-auto">
      <div className="p-3 flex items-center gap-2">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold">YB</div>
        <div>
          <p className="font-semibold text-sm text-slate-900">Your Brand</p>
          <p className="text-xs text-slate-500">Just now · 🌐</p>
        </div>
      </div>
      <div className="px-3 pb-3">
        <p className="text-sm text-slate-800 whitespace-pre-wrap">{post.content || 'No content'}</p>
        {post.hashtags?.length > 0 && (
          <p className="text-blue-600 text-sm mt-2">{post.hashtags.join(' ')}</p>
        )}
      </div>
      {renderMediaPreview('aspect-video')}
      {post.link && (
        <div className="border-t border-slate-100 p-3 bg-slate-50">
          <p className="text-xs text-slate-500 uppercase tracking-wide">yourwebsite.com</p>
          <p className="text-sm font-medium text-slate-900 mt-1">Link Preview</p>
        </div>
      )}
      <div className="border-t border-slate-100 px-3 py-2 flex items-center justify-between text-slate-500 text-xs">
        <button className="hover:text-blue-500 transition-colors" onClick={() => previewToast('Facebook')} data-testid="preview-like-btn">👍 Like</button>
        <button className="hover:text-blue-500 transition-colors" onClick={() => previewToast('Facebook')} data-testid="preview-comment-btn">💬 Comment</button>
        <button className="hover:text-blue-500 transition-colors" onClick={() => previewToast('Facebook')} data-testid="preview-share-btn">↗️ Share</button>
      </div>
    </div>
  </TabsContent>
);

export const InstagramPreview = ({ post, renderMediaPreview }) => (
  <TabsContent value="instagram">
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden max-w-md mx-auto">
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 via-red-500 to-yellow-500 p-[2px]">
            <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-xs font-bold">YB</div>
          </div>
          <span className="font-semibold text-sm">yourbrand</span>
        </div>
        <span className="text-slate-400">•••</span>
      </div>
      {post.media_urls?.length > 0 ? (
        renderMediaPreview('aspect-square')
      ) : (
        <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-slate-400">
          <Image className="w-12 h-12" />
        </div>
      )}
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-4 text-slate-800">
          <button onClick={() => previewToast('Instagram')} className="hover:text-red-500 transition-colors cursor-pointer">♡</button>
          <button onClick={() => previewToast('Instagram')} className="hover:text-blue-500 transition-colors cursor-pointer">💬</button>
          <button onClick={() => previewToast('Instagram')} className="hover:text-blue-500 transition-colors cursor-pointer">✈️</button>
        </div>
        <p className="text-sm">
          <span className="font-semibold">yourbrand</span>{' '}
          <span className="text-slate-800">
            {post.content?.substring(0, 125) || 'Your caption here...'}
            {post.content?.length > 125 && '...'}
          </span>
        </p>
        {post.hashtags?.length > 0 && (
          <p className="text-blue-900 text-sm">{post.hashtags.slice(0, 5).join(' ')}</p>
        )}
      </div>
    </div>
  </TabsContent>
);

export const TwitterPreview = ({ post, renderMediaPreview }) => (
  <TabsContent value="twitter">
    <div className="bg-white rounded-xl border border-slate-200 p-4 max-w-md mx-auto">
      <div className="flex gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">YB</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <span className="font-bold text-sm">Your Brand</span>
            <span className="text-blue-500">✓</span>
            <span className="text-slate-500 text-sm">@yourbrand · 1m</span>
          </div>
          <p className="text-sm mt-1 text-slate-800 whitespace-pre-wrap">
            {post.content?.substring(0, 280) || 'Your post here...'}
          </p>
          {post.hashtags?.length > 0 && (
            <p className="text-blue-500 text-sm mt-1">{post.hashtags.slice(0, 3).join(' ')}</p>
          )}
          {post.media_urls?.length > 0 && (
            <div className="mt-3 rounded-xl overflow-hidden">{renderMediaPreview('aspect-video')}</div>
          )}
          <div className="flex items-center justify-between mt-3 text-slate-500 text-sm">
            <button onClick={() => previewToast('X')} className="hover:text-blue-500 transition-colors cursor-pointer">💬</button>
            <button onClick={() => previewToast('X')} className="hover:text-green-500 transition-colors cursor-pointer">🔁</button>
            <button onClick={() => previewToast('X')} className="hover:text-red-500 transition-colors cursor-pointer">♡</button>
            <button onClick={() => previewToast('X')} className="hover:text-blue-500 transition-colors cursor-pointer">📊</button>
            <button onClick={() => previewToast('X')} className="hover:text-blue-500 transition-colors cursor-pointer">↗️</button>
          </div>
        </div>
      </div>
    </div>
  </TabsContent>
);

export const LinkedInPreview = ({ post, renderMediaPreview }) => (
  <TabsContent value="linkedin">
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden max-w-md mx-auto">
      <div className="p-3 flex items-center gap-2">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-bold">YB</div>
        <div>
          <p className="font-semibold text-sm text-slate-900">Your Brand</p>
          <p className="text-xs text-slate-500">10,000 followers</p>
          <p className="text-xs text-slate-400">Just now · 🌐</p>
        </div>
      </div>
      <div className="px-3 pb-3">
        <p className="text-sm text-slate-800 whitespace-pre-wrap">{post.content || 'Your content will appear here...'}</p>
        {post.hashtags?.length > 0 && <p className="text-blue-600 text-sm mt-2">{post.hashtags.join(' ')}</p>}
      </div>
      {renderMediaPreview('aspect-video')}
      <div className="border-t border-slate-100 px-3 py-2 flex items-center gap-4 text-slate-500 text-xs">
        <button onClick={() => previewToast('LinkedIn')} className="hover:text-blue-600 transition-colors cursor-pointer">👍 Like</button>
        <button onClick={() => previewToast('LinkedIn')} className="hover:text-blue-600 transition-colors cursor-pointer">💬 Comment</button>
        <button onClick={() => previewToast('LinkedIn')} className="hover:text-blue-600 transition-colors cursor-pointer">🔁 Repost</button>
        <button onClick={() => previewToast('LinkedIn')} className="hover:text-blue-600 transition-colors cursor-pointer">✈️ Send</button>
      </div>
    </div>
  </TabsContent>
);

export const TikTokPreview = ({ post }) => (
  <TabsContent value="tiktok">
    <div className="bg-black rounded-lg overflow-hidden max-w-[280px] mx-auto aspect-[9/16] relative">
      {post.media_urls?.length > 0 ? (
        <div className="absolute inset-0">
          {isVideoUrl(post.media_urls[0]) ? (
            <>
              <img src={getVideoThumbnailUrl(post.media_urls[0])} alt="Video" className="w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                  <Play className="h-8 w-8 text-white ml-1" />
                </div>
              </div>
            </>
          ) : (
            <img src={post.media_urls[0]} alt="Media" className="w-full h-full object-cover" />
          )}
        </div>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-b from-slate-800 to-slate-900 flex items-center justify-center">
          <Video className="w-16 h-16 text-slate-600" />
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
        <p className="text-white text-sm font-semibold">@yourbrand</p>
        <p className="text-white/90 text-xs mt-1 line-clamp-2">{post.content?.substring(0, 100) || 'Your caption...'}</p>
        {post.hashtags?.length > 0 && (
          <p className="text-white/80 text-xs mt-1">{post.hashtags.slice(0, 3).join(' ')}</p>
        )}
      </div>
      <div className="absolute right-2 bottom-20 flex flex-col items-center gap-4">
        <button className="text-center cursor-pointer" onClick={() => previewToast('TikTok')}>
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors">
            <Heart className="h-5 w-5 text-white" />
          </div>
          <span className="text-white text-xs">1.2K</span>
        </button>
        <button className="text-center cursor-pointer" onClick={() => previewToast('TikTok')}>
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors">
            <MessageSquare className="h-5 w-5 text-white" />
          </div>
          <span className="text-white text-xs">89</span>
        </button>
        <button className="text-center cursor-pointer" onClick={() => previewToast('TikTok')}>
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors">
            <Share2 className="h-5 w-5 text-white" />
          </div>
          <span className="text-white text-xs">45</span>
        </button>
      </div>
    </div>
  </TabsContent>
);

export const YouTubePreview = ({ post }) => (
  <TabsContent value="youtube">
    <div className="bg-white rounded-lg overflow-hidden max-w-md mx-auto">
      <div className="aspect-video bg-slate-900 relative">
        {post.media_urls?.length > 0 ? (
          <>
            {isVideoUrl(post.media_urls[0]) ? (
              <img src={getVideoThumbnailUrl(post.media_urls[0])} alt="Video thumbnail" className="w-full h-full object-cover" />
            ) : (
              <img src={post.media_urls[0]} alt="Media" className="w-full h-full object-cover" />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                <Play className="h-8 w-8 text-white ml-1" />
              </div>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-600">
            <Youtube className="w-16 h-16" />
          </div>
        )}
        <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1 rounded">0:30</div>
      </div>
      <div className="p-3">
        <p className="font-semibold text-sm line-clamp-2">{post.content?.substring(0, 60) || 'Video Title'}</p>
        <p className="text-xs text-slate-500 mt-1">Your Brand • 1.2K views • 1 hour ago</p>
      </div>
    </div>
  </TabsContent>
);

export const PinterestPreview = ({ post }) => (
  <TabsContent value="pinterest">
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg max-w-[240px] mx-auto">
      <div className="aspect-[2/3] bg-slate-100 relative">
        {post.media_urls?.length > 0 ? (
          <img src={post.media_urls[0]} alt="Pin" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <Image className="w-12 h-12" />
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="font-semibold text-sm line-clamp-2">{post.content?.substring(0, 50) || 'Pin Title'}</p>
        <p className="text-xs text-slate-500 mt-1">yourbrand</p>
      </div>
    </div>
  </TabsContent>
);

/**
 * Generic fallback preview for platforms that don't have a custom mockup
 * (threads, bluesky, snapchat, etc.).
 */
export const GenericPreview = ({ platformId, post, platformInfo, renderMediaPreview }) => {
  const PlatformIcon = platformInfo?.icon;
  return (
    <TabsContent key={platformId} value={platformId}>
      <div className="bg-white rounded-lg border border-slate-200 p-4 max-w-md mx-auto">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
            {PlatformIcon ? (
              <PlatformIcon className="h-5 w-5" style={{ color: platformInfo.color }} />
            ) : (
              <span>{platformInfo?.emoji}</span>
            )}
          </div>
          <div>
            <p className="font-semibold text-sm">Your Brand</p>
            <p className="text-xs text-slate-500">Just now</p>
          </div>
        </div>
        <p className="text-sm text-slate-800 whitespace-pre-wrap">{post.content || 'Your content here...'}</p>
        {post.hashtags?.length > 0 && (
          <p className="text-blue-600 text-sm mt-2">{post.hashtags.join(' ')}</p>
        )}
        {post.media_urls?.length > 0 && (
          <div className="mt-3">{renderMediaPreview('aspect-video')}</div>
        )}
      </div>
    </TabsContent>
  );
};
